import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjectDetailDTO } from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, Panel, SectionHeader, toneColor } from "@/design/primitives";
import { kindLabel, lifecycleLabel, lifecycleTone, projectTone } from "@/design/project-visual";
import { canManage, useProjectRole } from "./project-data";
import { OverviewTab } from "./tabs/OverviewTab";
import { KanbanTab } from "./tabs/KanbanTab";
import { PlanTab } from "./tabs/PlanTab";
import { ProjectGoalsTab } from "./tabs/ProjectGoalsTab";
import { ReleasesTab } from "./tabs/ReleasesTab";
import { KnowledgeTab } from "./tabs/KnowledgeTab";
import { ProjectBrainTab } from "../brain/ProjectBrainTab";
import { AppsTab } from "./tabs/AppsTab";
import { CodebasesTab } from "./tabs/CodebasesTab";
import { ProjectSettingsTab } from "./tabs/ProjectSettingsTab";
import { ProjectAuditTab } from "./tabs/ProjectAuditTab";
import { ConnectionsTab } from "./tabs/ConnectionsTab";
import { IncidentsTab } from "./tabs/IncidentsTab";
import { PitchPerfectTab } from "./tabs/PitchPerfectTab";
import { FeedbackTab } from "./tabs/FeedbackTab";

export type ProjectTabKey =
  | "overview"
  | "kanban"
  | "plan"
  | "knowledge"
  | "brain"
  | "apps"
  | "codebases"
  | "goals"
  | "releases"
  | "connections"
  | "incidents"
  | "pitch"
  | "feedback"
  | "audit"
  | "settings";

/**
 * Pořadí tabů. Projekt má vždycky všechny — zapínání modulů bylo jen matoucí
 * nastavení navíc: prázdný tab řekne rovnou, čím se naplní, kdežto skrytý tab
 * vypadá jako chybějící funkce. `managerOnly` drží nastavení mimo dohled klienta.
 */
const TAB_ORDER: { key: ProjectTabKey; managerOnly?: boolean; label: string }[] = [
  { key: "overview", label: "OVERVIEW" },
  { key: "kanban", label: "KANBAN" },
  { key: "plan", label: "PLÁN VÝVOJE" },
  { key: "pitch", label: "PITCH PERFECT" },
  { key: "feedback", label: "FEEDBACK" },
  { key: "knowledge", label: "KNOWLEDGE" },
  { key: "brain", label: "BRAIN" },
  { key: "apps", label: "APPS" },
  { key: "codebases", label: "CODEBASES" },
  { key: "goals", label: "GOALS" },
  { key: "releases", label: "RELEASES" },
  { key: "connections", label: "CONNECTIONS" },
  { key: "incidents", label: "INCIDENTS" },
  { key: "audit", label: "AUDIT" },
  { key: "settings", managerOnly: true, label: "SETTINGS" },
];

export function ProjectDetailPage() {
  const params = useParams({ strict: false }) as { slug: string; tab?: string };
  const slug = params.slug;
  const query = useQuery({
    queryKey: ["projects", slug],
    queryFn: () => api.project(slug),
    retry: (count, err) =>
      !(err instanceof ApiRequestError && (err.status === 403 || err.status === 404)) && count < 2,
  });

  if (query.error instanceof ApiRequestError) {
    return (
      <Panel className="mx-auto mt-16 max-w-md p-6 text-center" data-testid="project-error">
        <div className="af-label" style={{ color: "var(--critical)" }}>
          {query.error.status === 403 ? "PŘÍSTUP ODEPŘEN" : "NENALEZENO"}
        </div>
        <p className="mt-2 text-[11px]" style={{ color: "var(--text-secondary)" }}>
          {query.error.status === 403 ? cs.common.forbidden : "Projekt neexistuje."}
        </p>
        <Link to="/projects" className="mt-4 inline-block text-[12px] tracking-[0.14em]">
          {cs.project.back}
        </Link>
      </Panel>
    );
  }

  const project = query.data?.project;
  if (!project) return null;
  return <ProjectDetail project={project} tabParam={params.tab} />;
}

function ProjectDetail({ project, tabParam }: { project: ProjectDetailDTO; tabParam?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useProjectRole(project);
  const manager = canManage(role);
  const inbox = useQuery({
    queryKey: ["inbox", project.id],
    queryFn: () => api.decisionInbox(project.id),
  });
  const openConflicts = (inbox.data?.items ?? []).length;
  // stat strip měl čtyři buňky s poznámkou „Fáze 3"; data pro ně existují
  const automation = useQuery({
    queryKey: ["section", project.id, "automation"],
    queryFn: () => api.projectAutomation(project.id),
    refetchInterval: 30_000,
  });
  const milestones = useQuery({
    queryKey: ["section", project.id, "milestones"],
    queryFn: () => api.milestones(project.id),
  });
  const milestone = (milestones.data?.milestones ?? []).find((m) => m.status === "OPEN") ?? null;
  const [healthOpen, setHealthOpen] = useState(false);
  const auto = automation.data;
  const health = auto?.health;
  const urgentDecisions = (inbox.data?.items ?? []).filter((i) => i.urgency === "URGENT").length;

  const tabs = TAB_ORDER.filter((t) => !t.managerOnly || manager);
  const activeKey: ProjectTabKey = tabs.some((t) => t.key === tabParam)
    ? (tabParam as ProjectTabKey)
    : "overview";

  const pauseMutation = useMutation({
    mutationFn: () =>
      api.updateProject(project.slug, {
        lifecycle: project.lifecycle === "PAUSED" ? "ACTIVE_DEV" : "PAUSED",
        reason:
          project.lifecycle === "PAUSED" ? "obnoveno z hlavičky projektu" : "pozastaveno z hlavičky projektu",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });

  const goTab = (key: ProjectTabKey) => {
    if (key === "overview") {
      void navigate({ to: "/projects/$slug", params: { slug: project.slug } });
    } else {
      void navigate({ to: "/projects/$slug/$tab", params: { slug: project.slug, tab: key } });
    }
  };

  return (
    <div className="af-animate-fade">
      {/* hlavička dle /Design: zpět · název · status · tag · pauza — vpravo taby */}
      {/* taby zůstávají pod hlavičkou na každé šířce — na širokém monitoru
          se dřív přesunuly doprava vedle názvu a bylo je třeba hledat jinde
          než na úzkém */}
      <div className="flex flex-col gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <Link
            to="/projects"
            className="shrink-0 text-[12px]"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.project.back}
          </Link>
          <h1
            className="af-display truncate text-[28px] font-bold leading-tight tracking-[-0.01em]"
            style={{ color: "var(--text-primary)" }}
          >
            {project.name}
          </h1>
          <Chip tone={lifecycleTone[project.lifecycle]}>{lifecycleLabel[project.lifecycle]}</Chip>
          {project.factoryReadyAt ? (
            <Chip tone="green">{cs.project.factoryReady}</Chip>
          ) : null}
          {kindLabel[project.kind] ? (
            <span
              className="af-mono shrink-0 px-3 py-[3px] text-[10px]"
              style={{
                borderRadius: "var(--radius-pill)",
                border: "1px solid var(--border-default)",
                color: "var(--text-tertiary)",
              }}
            >
              {kindLabel[project.kind]}
            </span>
          ) : null}
          {manager ? (
            <Btn
              variant="danger"
              tone="amber"
              className="shrink-0"
              onClick={() => pauseMutation.mutate()}
              disabled={pauseMutation.isPending}
              data-testid="project-pause"
            >
              {project.lifecycle === "PAUSED" ? `▶ ${cs.project.resume}` : `⏸ ${cs.project.pause}`}
            </Btn>
          ) : null}
        </div>
        {/* tab bar — na mobilu horizontálně scrollovatelný, ať jsou dosažitelné všechny taby */}
        {/* tab bar — scrollovatelný na každé šířce: s rostoucím počtem tabů by
            xl:shrink-0 + overflow-visible přetékal do hlavičky projektu */}
        <div className="flex min-w-0 gap-4 overflow-x-auto whitespace-nowrap text-[11px] tracking-[0.14em]">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => goTab(t.key)}
              className="flex-none cursor-pointer pb-1 uppercase"
              style={{
                color: activeKey === t.key ? "var(--accent)" : "var(--text-secondary)",
                borderBottom:
                  activeKey === t.key ? "1px solid var(--accent)" : "1px solid transparent",
              }}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* stat strip — 7 dlaždic dle p2a */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <StripCell label={cs.project.health} dimNote={cs.project.healthUnknown}>
          {health?.score != null ? (
            <>
              <button
                type="button"
                className="af-display cursor-pointer text-[24px] font-bold leading-none"
                style={{
                  color:
                    health.score >= 80
                      ? "var(--positive)"
                      : health.score >= 60
                        ? "var(--text-primary)"
                        : "var(--warning)",
                }}
                onClick={() => setHealthOpen((v) => !v)}
                data-testid="strip-health"
              >
                {health.score}
              </button>
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {health.factors.length === 0
                  ? cs.project.healthClean
                  : health.factors
                      .slice(0, 2)
                      .map((f) => f.label)
                      .join(" · ")}{" "}
                <button
                  type="button"
                  className="cursor-pointer"
                  style={{ color: "var(--accent)" }}
                  onClick={() => setHealthOpen((v) => !v)}
                >
                  {cs.project.breakdown} →
                </button>
              </div>
            </>
          ) : undefined}
        </StripCell>
        <StripCell label={cs.project.attention}>
          <span
            className="af-display text-[24px] font-bold leading-none"
            style={{
              color:
                projectTone(project) === "dim"
                  ? "var(--text-primary)"
                  : toneColor[projectTone(project)],
            }}
          >
            {project.attentionScore}
          </span>
        </StripCell>
        <StripCell label={cs.project.milestone} dimNote={cs.project.noMilestone}>
          {milestone ? (
            <>
              <span className="af-card-title">{milestone.title}</span>
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {milestone.doneTaskCount}/{milestone.taskCount}
                {milestone.deadline
                  ? ` · ${new Date(milestone.deadline).toLocaleDateString("cs-CZ")}`
                  : ""}
              </div>
            </>
          ) : undefined}
        </StripCell>
        <StripCell label={cs.project.factoryReadyLabel} dimNote={cs.project.notReadyYet}>
          {auto?.readiness.factoryReadyAt ? (
            <>
              <span className="af-card-title" style={{ color: "var(--positive)" }}>
                ★ {new Date(auto.readiness.factoryReadyAt).toLocaleDateString("cs-CZ")}
              </span>
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {cs.project.devDone}
              </div>
            </>
          ) : auto ? (
            <>
              <span className="af-display text-[24px] font-bold leading-none">
                {auto.readiness.planDone}/{auto.readiness.planTotal || "—"}
              </span>
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {cs.project.planTicked.toLowerCase()}
              </div>
            </>
          ) : undefined}
        </StripCell>
        <StripCell label={cs.project.activeTasks}>
          {auto ? (
            <>
              <span className="af-display text-[24px] font-bold leading-none" style={{ color: "var(--accent)" }}>
                {auto.running.length}
              </span>
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {cs.project.inQa(auto.readiness.inQa)}
              </div>
            </>
          ) : undefined}
        </StripCell>
        <StripCell label={cs.project.blocked}>
          {auto ? (
            <>
              <span
                className="af-display text-[24px] font-bold leading-none"
                style={{ color: auto.blocked.length ? "var(--warning)" : "var(--text-primary)" }}
              >
                {auto.blocked.length}
              </span>
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {auto.blocked.length ? cs.project.blockedNote : cs.project.nothingBlocked}
              </div>
            </>
          ) : undefined}
        </StripCell>
        <StripCell label={cs.project.decisions}>
          <span
            className="af-display text-[24px] font-bold leading-none"
            style={{ color: openConflicts > 0 ? "var(--warning)" : "var(--text-primary)" }}
            data-testid="strip-decisions"
          >
            {openConflicts}
          </span>
          {openConflicts ? (
            <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {urgentDecisions ? cs.project.urgentDecisions(urgentDecisions) : cs.project.toInbox}
            </div>
          ) : null}
        </StripCell>
      </div>

      {healthOpen && health?.score != null ? (
        <Panel className="mt-3 p-4" data-testid="health-breakdown">
          <SectionHeader
            label={cs.project.healthBreakdown}
            sub={`// ${health.score}/100`}
            right={
              <button type="button" onClick={() => setHealthOpen(false)}>
                {cs.common.close}
              </button>
            }
          />
          {health.factors.length === 0 ? (
            <div className="mt-2 text-[13px]" style={{ color: "var(--positive)" }}>
              ✓ {cs.project.healthClean}
            </div>
          ) : (
            <div className="mt-3 flex flex-col gap-2">
              {health.factors.map((f) => (
                <div key={f.key} className="flex items-baseline justify-between gap-3 text-[13px]">
                  <span style={{ color: "var(--text-secondary)" }}>{f.label}</span>
                  <span className="af-mono shrink-0" style={{ color: "var(--warning)" }}>
                    {f.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      ) : null}

      <TabContent project={project} tab={activeKey} goTab={goTab} />
    </div>
  );
}

function StripCell({
  label,
  children,
  dimNote,
}: {
  label: string;
  children?: ReactNode;
  dimNote?: string;
}) {
  return (
    <div
      className="px-3.5 py-3"
      style={{
        borderRadius: "var(--radius-box)",
        border: "1px solid var(--border-default)",
        background: "var(--surface)",
        backdropFilter: "var(--surface-blur)",
      }}
    >
      <div className="af-mono text-[9px] tracking-[0.14em] uppercase" style={{ color: "var(--text-tertiary)" }}>
        {label}
      </div>
      <div className="mt-1">
        {children ?? (
          <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            — <span className="af-mono text-[10px]">{dimNote}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function TabContent({
  project,
  tab,
  goTab,
}: {
  project: ProjectDetailDTO;
  tab: ProjectTabKey;
  goTab: (key: ProjectTabKey) => void;
}) {
  switch (tab) {
    case "overview":
      return <OverviewTab project={project} goTab={goTab} />;
    case "knowledge":
      return <KnowledgeTab project={project} />;
    case "brain":
      return <ProjectBrainTab project={project} />;
    case "apps":
      return <AppsTab project={project} />;
    case "codebases":
      return <CodebasesTab project={project} />;
    case "settings":
      return <ProjectSettingsTab project={project} />;
    case "audit":
      return <ProjectAuditTab project={project} />;
    case "kanban":
      return <KanbanTab project={project} />;
    case "plan":
      return <PlanTab project={project} />;
    case "goals":
      return <ProjectGoalsTab project={project} />;
    case "releases":
      return <ReleasesTab project={project} />;
    case "connections":
      return <ConnectionsTab project={project} />;
    case "incidents":
      return <IncidentsTab project={project} />;
    case "pitch":
      return <PitchPerfectTab project={project} />;
    case "feedback":
      return <FeedbackTab project={project} />;
    default:
      return null;
  }
}

/** Sdílený vzhled akčních tlačítek pro záložky (export, přidání…). */
export function TabActionBtn(props: Parameters<typeof Btn>[0]) {
  return <Btn {...props} className={`!px-3 !py-1.5 ${props.className ?? ""}`} />;
}
